import React from 'react'

import { formatCurrency, formatPrice } from '../lib/currency-formatter'

// import TradeButton from './TradeButton';
import NumberInput from './NumberInput'
import TradeSettings from './TradeSettings'
import { I18n, Translate } from 'react-redux-i18n'
import { AccountDetails, ChartMode, TradeMode, TradingPane as TradePane } from '../types'
import {
  calcAmountValue,
  PriceExtended,
  TradeCommandType,
  TradingAccount,
} from '@bdswiss/mt4-connector'
import HelpTooltip from './HelpTooltip'
import Spinner from './Spinner'
import { getLeverageForSecurity } from '../utils/common-utils'

export interface Props {
  accountLoaded: boolean
  assetLoaded: boolean
  opened: boolean
  account?: TradingAccount
  asset: PriceExtended
  locale: string
  paneValues: TradePane
  chartMode: ChartMode
  favourites: string[]
  onSetTradeType: (tradeType: TradeCommandType) => void
  onSetAmount: (amount: number) => void
  onSetTakeProfit: (takeProfit: number) => void
  onSetStopLoss: (stopLoss: number) => void
  onSetPrice: (price: number) => void
  onTrade: (tradeType: TradeCommandType) => void
  onToggleMenu: () => void
  onSetChartMode: () => void
  onSelectPosition: (order: number, expanded?: boolean) => void
  onInputAmount: (amount: number) => void
  onToggleFavourite: (favourites: string[]) => void
  userAccount?: AccountDetails,
}

export interface State {
  favourites: { [symbol: string]: { confirmed: boolean; operation: 'on' | 'off' } } // true if confirmed from server
  inputValid: boolean
}

const TradingPane = (props: Props) => {
  return <TradingPaneContent {...props} />
}

class TradingPaneContent extends React.Component<Props, State> {
  private requiredMargin = React.createRef<HTMLElement>()
  private transformTimeout?: number
  private classTimeout?: number

  constructor(props: Props) {
    super(props)

    this.setChartMode = this.setChartMode.bind(this)
    this.onChangeAmount = this.onChangeAmount.bind(this)
    this.toggleFavourite = this.toggleFavourite.bind(this)

    this.state = {
      favourites: this.props.favourites.reduce((acc, cur) => {
        acc[cur] = { confirmed: true, operation: 'on' }
        return acc
      }, {} as any),
      inputValid: true,
    }
  }

  public render() {
    const {
      opened,
      account,
      asset,
      accountLoaded,
      assetLoaded,
      locale,
      onTrade,
      // onSetAmount,
      onInputAmount,
      onSetTakeProfit,
      onSetStopLoss,
      onSetPrice,
      paneValues,
      onToggleMenu,
      onSetTradeType,
      userAccount
    } = this.props
    const { inputValid } = this.state
    if (!account || !accountLoaded || !assetLoaded) {
      return (
        <div className={'sidebar' + (opened ? ' show' : '')}>
          <Spinner />
        </div>
      )
    }
    const { amountInitial, amount, tradeOperationEnabled, tradeType, lots } = paneValues
    const { value, valueAccount, requiredMargin, requiredMarginAccount } = paneValues[tradeType]
    const {
      digits,
      mbid,
      mask,
      symbol,
      lotStep,
      lotSize,
      contractType,
      currency,
      marginCurrency,
      dailyReturnBid,
      dailyReturnAsk,
      lotMin,
      lotMax,
      security,
    } = asset
    const accountCurrency = account.currency
    const spread = (mask - mbid).toFixed(digits)
    const amountFraction = (lotStep * lotSize).toString().split('.')[1]
    const dailyReturn =
      tradeType === 'buy'
        ? dailyReturnBid
          ? dailyReturnBid
          : 0
        : dailyReturnAsk
        ? dailyReturnAsk
        : 0
    const favourite = Object.keys(this.state.favourites).find(key => key === symbol)
    const confirmed = favourite ? this.state.favourites[favourite].confirmed : true
    let leverage = getLeverageForSecurity(account, asset).toFixed(0)
    console.log('TradingPane account', userAccount)
    console.log('Asset symbol', asset.symbol)
    if (userAccount) {
      const leverageOverride = (userAccount.symbolOverrides ?? []).find(
        override => override.symbol === asset.symbol
      )
      console.log('leverageOverride', leverageOverride)
      if (leverageOverride) {
        leverage = leverageOverride.marginDivider.toFixed(0)
      }
    }
    return (
      <div className={'tradingPane flex-column' + (opened ? ' show' : '')} data-cy="tradingPane">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="sidebar__title" data-cy="tradeSymbol">
            <i
              onClick={() => this.toggleFavourite(symbol)}
              className={`favourite favourite-trade ${
                favourite ? 'icon-star-full' : 'icon-star-empty'
              } ${!confirmed ? 'unconfirmed' : ''}`}
              title="Favourite"
            />
            {security.startsWith('DMA') && (
              <div className="leveraged-label">
                {I18n.t(Number(leverage) > 1 ? 'leveragedLabel' : 'unleveragedLabel')}
              </div>
            )}
            {I18n.t(tradeType)}&nbsp;&nbsp;{(asset as any).label}
          </h2>
          <button onClick={onToggleMenu} className="btn close_btn" data-cy="closeTrade">
            <i className="icon-close-new" />
          </button>
        </div>
        <div onClick={this.setChartMode}>
          <div className="d-flex flex-row justify-content-between font-weight-medium">
            <div>
              <span className="sidebar__label">
                <Translate value={'currentPrice'} />:{' '}
              </span>
              <span>{formatPrice(/buy/i.test(tradeType) ? mask : mbid, digits, '')}</span>
            </div>
            <div
              className={
                'percentage-difference d-flex flex-row align-items-center ' +
                (dailyReturn < 0 ? 'negative' : 'positive')
              }
            >
              {(dailyReturn < 0 ? '' : '+') + dailyReturn.toFixed(2)}%
              <i className={dailyReturn < 0 ? 'icon-arrow-down' : 'icon-arrow-up'} />
            </div>
          </div>
          <div className="d-flex flex-row justify-content-between font-weight-medium">
            <div className="d-flex">
              <span className="d-flex sidebar__label">
                <Translate value={'spread'} />: &nbsp;
              </span>
              <span>{spread}</span>
              <HelpTooltip content="helpSpread" />
            </div>
          </div>
          {/* <div className="btn-group btn-group-toggle arrow-btn-group d-flex flex-row pt-3" data-toggle="buttons">
            <TradeButton tradeType="sell" tradePrice={formatPrice(mbid, digits, asset.currency)} onTrade={onSetTradeType.bind(null,'sell')} active={tradeType==='sell'}/>
            <TradeButton tradeType="buy" tradePrice={formatPrice(mask, digits, asset.currency)} onTrade={onSetTradeType.bind(null,'buy')} active={tradeType==='buy'}/>
        </div>
        <div className="sidebar__spread text-center">
						<div className="sidebar__spread-text">
              <HelpTooltip content="helpSpread"/><Translate value={"spread"}/>:
              <span> {spread}</span>
						</div>
				</div> */}
          <div className="d-flex flex-row justify-content-between mt-3 mb-2">
            <div>
              <span className="sidebar__label trade-input-label">
                <Translate value={'amount'} />: {contractType}
              </span>
            </div>
            <div className="d-flex sidebar__label">
              <span className="d-flex">
                <HelpTooltip content="helpLeverage" />
              </span>
              <Translate value={'leverage'} />
              <span>
                :&nbsp; 1:
                {leverage}
              </span>
            </div>
          </div>
          <div className="counter-group">
            <NumberInput
              valueInitial={amountInitial}
              placeholder={I18n.t('amount')}
              value={amount}
              precision={amountFraction ? amountFraction.length : 0}
              step={lotStep * lotSize}
              setValue={val => this.onChangeAmount(+val)}
              calcValue={calcAmountValue.bind(null, asset)}
              valueChanged={onInputAmount}
              onInputValid={this.setInputValidityStatus}
              min={lotMin * lotSize}
              max={lotMax * lotSize}
            />
          </div>
          <div className="mt-4 mb-3">
            <div className="d-flex flex-row justify-content-between my-2 pb-1">
              <span className="d-flex sidebar__label">
                <Translate value={'value'} />
                <HelpTooltip content="helpValue" />
              </span>
              <span className="sidebar__label lots-amount">
                (<Translate value={'lotsAmount'} />: {lots})
              </span>
            </div>
            <div className="font-weight-medium" data-cy="value">
              {formatCurrency(value, marginCurrency, locale) +
                (marginCurrency !== accountCurrency
                  ? ' = ' + formatCurrency(valueAccount, accountCurrency, locale)
                  : '')}
            </div>
          </div>
          <div className="mt-3 mb-2">
            <div className="d-flex sidebar__label">
              <Translate value={'requiredMargin'} />
              <HelpTooltip content="helpInitialMargin" />
            </div>
            <div className="font-weight-medium" data-cy="requiredMargin">
              <span ref={this.requiredMargin}>
                {formatCurrency(requiredMargin, marginCurrency, locale) +
                  (currency !== accountCurrency
                    ? ' = ' + formatCurrency(requiredMarginAccount, accountCurrency, locale)
                    : '')}
              </span>
            </div>
          </div>
          <TradeSettings
            key={tradeType + symbol}
            account={account}
            asset={asset}
            locale={locale}
            tradeType={tradeType}
            tradingValues={this.props.paneValues[tradeType]}
            onSetTakeProfit={onSetTakeProfit}
            onSetStopLoss={onSetStopLoss}
            onSetPrice={onSetPrice}
            accountLoaded={accountLoaded}
            onInputValid={this.setInputValidityStatus}
          />
          <div className="align-items-center d-flex mt-3 mb-4">
            <button
              onClick={onTrade.bind(null, tradeType)}
              disabled={
                !tradeOperationEnabled ||
                !inputValid ||
                account.freeMargin < requiredMarginAccount ||
                asset.trade === TradeMode.disabled
              }
              className="btn btn-primary flex-grow-1"
              data-cy="placeTrade"
            >
              <Translate
                value={
                  this.props.paneValues[tradeType].pendingOrderEnabled
                    ? 'scheduleOrder'
                    : 'placeTrade'
                }
              />
              &nbsp;(
              <Translate value={/buy/i.test(tradeType) ? 'buy' : 'sell'} />)
            </button>
          </div>
          <div
            className="text-primary cursor-pointer"
            onClick={
              /buy/i.test(tradeType)
                ? onSetTradeType.bind(null, 'sell')
                : onSetTradeType.bind(null, 'buy')
            }
          >
            {/buy/i.test(tradeType) &&
              [TradeMode.enabled, TradeMode.onlySell].includes(asset.trade) && (
                <Translate value="switchToSell" />
              )}
            {/sell/i.test(tradeType) &&
              [TradeMode.enabled, TradeMode.onlyBuy].includes(asset.trade) && (
                <Translate value="switchToBuy" />
              )}
          </div>
        </div>
      </div>
    )
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    const { favourites } = this.props
    if (prevProps.favourites !== favourites) {
      this.setState(() => ({
        favourites: favourites.reduce((acc, cur) => {
          acc[cur] = { confirmed: true, operation: 'on' }
          return acc
        }, {} as any),
      }))
    }
  }

  private setChartMode() {
    const { chartMode, onSetChartMode, onSelectPosition } = this.props
    if (chartMode !== 'trade') {
      onSetChartMode()
      onSelectPosition(-1, true)
    }
  }

  private onChangeAmount(amount: number) {
    const valueNode = this.requiredMargin.current
    if (valueNode) {
      valueNode.classList.add('value-animation')
      valueNode.style.color = '#007aff'
      valueNode.style.transform = 'scale(1.2)'
      this.transformTimeout && window.clearTimeout(this.transformTimeout)
      this.classTimeout && window.clearTimeout(this.classTimeout)
      this.transformTimeout = window.setTimeout(() => {
        valueNode.style.color = 'inherit'
        valueNode.style.transform = 'scale(1)'
      }, 300)
      this.classTimeout = window.setTimeout(() => {
        valueNode.classList.remove('value-animation')
      }, 600)
    }
    this.props.onSetAmount(amount)
  }

  private toggleFavourite(favourite: string) {
    const { favourites } = this.state
    if (Object.keys(favourites).includes(favourite)) {
      favourites[favourite] = { confirmed: false, operation: 'off' }
    } else {
      favourites[favourite] = { confirmed: false, operation: 'on' }
    }
    this.props.onToggleFavourite(
      Object.keys(favourites).filter(key => favourites[key].operation === 'on')
    )
    this.setState(() => ({
      favourites,
    }))
  }

  private setInputValidityStatus = (inputValid: boolean) => {
    this.setState({ inputValid })
  }
}

export default TradingPane
